import { Injectable } from '@angular/core';
import { BaseModalViewModel } from '../../../../../../../../models/base/base-modal-view-model';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { exists } from '../../../../../../../../functions/exists';
import { ToastService } from '../../../../../../../../modules/toasts/service/toast.service';
import { BrandDomainModel } from '../../../../../../../../domainModels/brand-domain-model';
import { ProductGroupDomainModel } from '../../../../../../../../domainModels/product-group-domain-model';
import { Brand } from '../../../../../../../../models/product/dto/brand';
import { ProductGroup } from '../../../../../../../../models/product/dto/product-group';
import { BsError } from '../../../../../../../../models/shared/bs-error';

@Injectable()
export class AddProductGroupsToBrandModalViewModel extends BaseModalViewModel {
  constructor(
    private brandDomainModel: BrandDomainModel,
    private productGroupDomainModel: ProductGroupDomainModel,
    private activeModal: NgbActiveModal,
    router: Router,
    ngbModal: NgbModal,
    private toastService: ToastService
  ) {
    super(router, ngbModal);
    this.productGroupDomainModel.fetchNonHydratedDisplayAttributeGroupsIfNotFullyLoaded();
    this.brandDomainModel.fetchBrandsIfEmpty();
  }

  private readonly _brand = new BehaviorSubject<Brand | null>(null);
  public readonly brand$ = this._brand as Observable<Brand | null>;
  connectToBrand = (brand: Brand) => this._brand.next(brand);

  public readonly brandName$ = this.brand$.pipe(
    map(brand => brand?.name ?? null),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private readonly _creationMode = new BehaviorSubject<boolean>(false);
  public readonly creationMode$ = this._creationMode as Observable<boolean>;
  connectToCreationMode = (creationMode: boolean) => this._creationMode.next(creationMode);

  public readonly title$ = this.brandName$.pipe(
    map(name => (exists(name) ? `Add Product Groups to ${name}` : 'Add Product Groups')),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly unassignedProductGroups$: Observable<ProductGroup[] | null> = combineLatest([
    this.productGroupDomainModel.productGroups$,
    this.brandDomainModel.brands$
  ]).pipe(
    map(([groups, brands]) => {
      if (groups === null || brands === null) return null;
      const linkedProductGroupIds = new Set(brands.flatMap(brand => brand?.displayAttributeGroupIds ?? []));
      return groups.filter(pg => exists(pg?.id) && !linkedProductGroupIds.has(pg.id));
    }),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private readonly _productGroupsToDisplay = new BehaviorSubject<ProductGroup[] | null>(null);
  public readonly productGroupsToDisplay$ = this._productGroupsToDisplay as Observable<ProductGroup[] | null>;
  connectToProductGroupsToDisplay = (productGroups: ProductGroup[] | null) =>
    this._productGroupsToDisplay.next(productGroups);

  private readonly _nItemsPerPage = new BehaviorSubject<number>(25);
  public readonly nItemsPerPage$ = this._nItemsPerPage as Observable<number>;
  connectToNItemsPerPage = (nItemsPerPage: number) => this._nItemsPerPage.next(nItemsPerPage);

  private readonly _productGroupIdsToBeAdded = new BehaviorSubject<string[]>([]);
  public readonly productGroupIdsToBeAdded$ = this._productGroupIdsToBeAdded as Observable<string[]>;
  connectToProductGroupIdsToBeAdded = (ids: string[]) => this._productGroupIdsToBeAdded.next(ids);

  public readonly nToAdd$ = this.productGroupIdsToBeAdded$.pipe(
    map(ids => ids?.length ?? 0),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly submitDisabled$ = combineLatest([this.nToAdd$, this.creationMode$]).pipe(
    map(([n, creationMode]) => n === 0 && !creationMode),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly primaryButtonText$ = combineLatest([this.nToAdd$, this.creationMode$]).pipe(
    map(([n, creationMode]) => {
      const countText = `${n} Product Group${n === 1 ? '' : 's'}`;
      if (creationMode) return `Create With ${countText}`;
      return n > 0 ? `Add ${countText}` : 'Add Product Groups';
    }),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public submit(): void {
    const lm = 'Adding Product Groups';
    this._loadingOpts.addRequest(lm);
    combineLatest([this.brand$, this.productGroupIdsToBeAdded$])
      .pipe(
        take(1),
        switchMap(([brand, pgIds]) => {
          if (!brand || !pgIds?.length) return of(null);
          return this.brandDomainModel.linkProductGroupsToBrand$(brand, pgIds);
        })
      )
      .subscribe({
        next: updatedBrand => {
          this._loadingOpts.removeRequest(lm);
          if (exists(updatedBrand)) {
            this.toastService.publishSuccessMessage('Product Groups Added in the Brand', 'Success');
          }
          this.activeModal.close(exists(updatedBrand));
        },
        error: (err: BsError) => {
          this._loadingOpts.removeRequest(lm);
          this.toastService.publishError(err);
        }
      });
  }
}
